import { setCookie, trace } from 'rytm-helpers'
import { KEY_CODE, isFunction, getFocusableChildren } from './lib/helpers'
import DialogController from './controllers/DialogController'

export const WCAG = {
  params: {
    // ## WCAG keyboard
    // class name added when any keyboard key pressed
    keyboardClassName: "wcag-using-keyboard",
    // class name added when TAB key pressed
    keyboardTabClassName: "wcag-using-tab",
    // ## WCGA contrast
    // contrast toggler selector
    contrastToggleSelector: ".wcag-contrast-toggle",
    // document class name for high contrast
    contrastClassName: "wcag-contrast",
    // user cookie name for high contrast (value 1 or 0)
    contrastCookieName: "r_wcag_contrast",
    // ## WCAG dialog focus trap
    // selector for "dialog toggler" (multiple dialogs allowed)
    dialogToggleSelector: "*[data-dialog-toggle]",
    // selector for "dialog toggler" (only one dialog allowed)
    dialogToggleOneSelector: "*[data-dialog-one]",
    // selector for "dialog close"
    dialogCloseSelector: "*[data-dialog-close]",
    // selector for "all dialogs close"
    dialogCloseAllSelector: "*[data-dialog-kill]",
    // ## First focus on view swap selector
    onSwapFocusToSelector: ".as-focus-to",
    // ## Focus to selector
    focusToSelector: "*[data-focus-to]",
    // Focusable heading selector
    focusFirstHeadingSelector: "h2"
  }, 
  events: {
    // On contrast change (by default toggle document's class name)
    onContrastChange: () => { 
      document.documentElement.classList.toggle(WCAG.params.contrastClassName) 
    },
    // On contrast enable
    onContrastEnable: false,
    // On contrast disable
    onContrastDisable: false,
    // On "Esc" key press (by default close all open dialogs)
    onEscKeyDown: () => {
      WCAG._closeLastOpenedDialog()
    }
  },
  dialogController: null
}
/**
 * ######################
 * ### PUBLIC methods ###
 */
WCAG.onViewChanged = () => {
  // make sure all dialogs are closed
  WCAG.dialogCloseAll()
  // set focus (for screen readers to notice swap)
  WCAG._setFocusOnViewSwap()
}
/**
 * Init with params and events
 * @param {Object} params 
 * @param {Object} events 
 */
WCAG.init = (params = {}, events = {}) => {
  for ( let param in params ) {
    if ( WCAG.params.hasOwnProperty(param) ) {
      WCAG.params[param] = params[param]
    }
  }
  for ( let event in events ) {
    if ( WCAG.events.hasOwnProperty(event) ) {
      WCAG.events[event] = events[event]
    }
  }
  WCAG._addEventListeners()
  WCAG.dialogController = new DialogController()
}
/**
 * Destroy WCAG
 */
WCAG.destroy = () => {
  WCAG._removeEventListeners()
  WCAG.dialogController.destroy()
  WCAG.dialogController = null
}
/**
 * Dialog OPEN
 * @param {String} id
 * @param {Object} params {dialog: el, toggleBtn: btn}
 */
WCAG.dialogOpen = (id, params) => {
  if (!id || !params.dialog || !params.toggleBtn) {
    console.warn("Dialog missing ID or params (expects: dialog, toggleBtn)")
    return
  }
  WCAG.dialogController.dialogOpen(id, params)
}
/**
 * Dialog CLOSE
 * @param {String} id
 */
WCAG.dialogClose = (id) => {
  WCAG.dialogController.dialogClose(id)
}
/**
 * Dialog CLOSE all
 */
WCAG.dialogCloseAll = () => {
  WCAG.dialogController.dialogCloseAll()
}
/**
 * #######################
 * ### PRIVATE methods ###
 */
/**
 * Add events listeners
 */
WCAG._addEventListeners = () => {
  // click handler
  document.addEventListener('click', WCAG._onDocumentClick, false)
  // Keyboard accessibility
  if (WCAG.params.keyboardClassName || WCAG.params.keyboardTabClassName) {
    document.addEventListener('keydown', WCAG._onAccessKeyboardAction, false)
    document.addEventListener('click', WCAG._onAccessMouseAction, false)
  }
}
/**
 * Remove events listeners
 */
WCAG._removeEventListeners = () => {
  // click handler
  document.removeEventListener('click', WCAG._onDocumentClick, false)
  // Keyboard accessibility
  if (WCAG.params.keyboardClassName) {
    document.removeEventListener('keydown', WCAG._onAccessKeyboardAction)
    document.removeEventListener('click', WCAG._onAccessMouseAction)
  }
}
// click event handler
WCAG._onDocumentClick = (e) => {
  // Contrast toggle
  if ( e.target.matches(WCAG.params.contrastToggleSelector)
      && WCAG.params.contrastClassName 
      && WCAG.params.contrastCookieName) {
    WCAG._toggleContrast(e)
  }
  // Dialog toggle (multiple allowed)
  if (e.target.matches(WCAG.params.dialogToggleSelector)
      && WCAG.params.dialogToggleSelector) {
    WCAG._onDialogToggleClick(e)
  }
  // Dialog toggle (one dialog allowed)
  if (e.target.matches(WCAG.params.dialogToggleOneSelector)
      && WCAG.params.dialogToggleOneSelector) {
    WCAG._onDialogToggleOneClick(e)
  }
  // Dialog close 
  if (e.target.matches(WCAG.params.dialogCloseSelector)
      && WCAG.params.dialogCloseSelector) {
    WCAG._onDialogCloseClick(e)
  }
  // Dialog kill (close all)
  if ( e.target.matches(WCAG.params.dialogCloseAllSelector)
      && WCAG.params.dialogCloseAllSelector) {
    WCAG._onDialogKillClick(e)
  }
  // Focus to
  if ( e.target.matches(WCAG.params.focusToSelector)
      && WCAG.params.focusToSelector) {
    WCAG._onFocusToClick(e)
  }
}


// set focus on view swap
WCAG._setFocusOnViewSwap = () => {
  if (WCAG.params.onSwapFocusToSelector) {
    const asFocus = document.querySelector(WCAG.params.onSwapFocusToSelector)
    if (asFocus) {
      asFocus.focus()
    }
  }
}
// Focus to... scroll + set focus
WCAG._onFocusToClick = (e) => {
  e.preventDefault()
  const selector = e.target.dataset.focusTo
  const target = selector ? document.querySelector(selector) : false
  if (target) {
    // scroll
    target.scrollIntoView()
    // set focus  
    const firstFocus = WCAG._getTargetFirstFocus(target)
    if (firstFocus) {
      firstFocus.focus()
    }
  }
}
WCAG._getTargetFirstFocus = (target) => {
  let ff
  if (WCAG.params.focusFirstHeadingSelector) {
    ff = target.querySelector(WCAG.params.focusFirstHeadingSelector)
    if (ff) {
      ff.tabIndex = 0
    }
  }
  if (!ff) {
    const list = getFocusableChildren(target)
    if (list.length > 0) {
      ff = list[0]
    }
  }
  return ff
}
/**
 * Dialog toggle (by specified target ID) - multiple allowed
 */
WCAG._onDialogToggleClick = (e) => {
  e.preventDefault()
  e.stopPropagation()
  const toggleBtn = e.target
  if (toggleBtn.dataset.dialogToggle && toggleBtn.dataset.target) {
    const dialogId = toggleBtn.dataset.dialogToggle
    const dialog = document.querySelector(toggleBtn.dataset.target)
    if (dialog) {
      if (!document.documentElement.classList.contains(dialogId)) {
        const params = {
          dialog,
          toggleBtn,
          onDialogOpen: () => {
            document.documentElement.classList.add(dialogId)
          },
          onDialogClose: () => {
            document.documentElement.classList.remove(dialogId)
          }
        }
        WCAG.dialogOpen(dialogId, params)
      } else {
        WCAG.dialogClose(dialogId)
      }
    } else {
      console.warn("[dialog] element is missing, selector:", trigger.dataset.target)
    }
  } else {
    console.warn("[dialog] trigger dataset 'target' is missing")
  }
}
/**
 * Dialog toggle - ONLY ONE dialog allowed
 */
WCAG._onDialogToggleOneClick = (e) => {
  e.preventDefault()
  e.stopPropagation()
  const toggleBtn = e.target
  if (toggleBtn.dataset.dialogOne && toggleBtn.dataset.target) {
    const dialogId = toggleBtn.dataset.dialogOne
    const dialog = document.querySelector(toggleBtn.dataset.target)
    if (dialog) {
      const wasOpen = document.documentElement.classList.contains(dialogId)
      WCAG.dialogCloseAll()
      if (!wasOpen) {
        const params = {
          dialog,
          toggleBtn,
          onDialogOpen: () => {
            document.documentElement.classList.add(dialogId)
          },
          onDialogClose: () => {
            document.documentElement.classList.remove(dialogId)
          }
        }
        WCAG.dialogOpen(dialogId, params)
      } 
    } else {
      console.warn("[dialog] element is missing, selector:", trigger.dataset.target)
    }
  } else {
    console.warn("[dialog] trigger dataset 'target' is missing")
  }
}
/**
 * Dialog close (by specified target ID)
 */
WCAG._onDialogCloseClick = (e) => {
  const trigger = e.target
  if (trigger.dataset.dialogClose) {
    e.preventDefault()
    e.stopPropagation()
    WCAG.dialogClose(trigger.dataset.dialogClose)
  }
}
/**
 * Dialog Kill (close all dialogs)
 */
WCAG._onDialogKillClick = (e) => {
  e.preventDefault();
  WCAG.dialogCloseAll()
}
// Close last opened dialog
WCAG._closeLastOpenedDialog = () => {
  if (WCAG.dialogController) {
    const dialog = WCAG.dialogController.getLastOpened()
    if (dialog) {
      WCAG.dialogClose(dialog.id)
    }
  }
}
// Any keyboard action
WCAG._onAccessKeyboardAction = (e) => {
  if (WCAG.params.keyboardClassName) {
    document.documentElement.classList.add(WCAG.params.keyboardClassName)
  }
  switch (e.keyCode) {
    case KEY_CODE.TAB:
      if (WCAG.params.keyboardTabClassName) {
        document.documentElement.classList.add(WCAG.params.keyboardTabClassName)
      }
      break;
    case KEY_CODE.ESC:
      if (isFunction(WCAG.events.onEscKeyDown)) {
        WCAG.events.onEscKeyDown()
      }
      break;
  }
}
// Any mouse action
WCAG._onAccessMouseAction = (e) => {
  if (WCAG.params.keyboardClassName) {
    document.documentElement.classList.remove(WCAG.params.keyboardClassName)
  }
  if (WCAG.params.keyboardTabClassName) {
    document.documentElement.classList.remove(WCAG.params.keyboardTabClassName)
  }
}
// Toggle Contrast
WCAG._toggleContrast = (e) => {
  e.preventDefault();
  let value = document.documentElement.classList.contains(WCAG.params.contrastClassName) ? 0 : 1
  setCookie(WCAG.params.contrastCookieName, value, 1)
  // propagate onContrastChange
  if (isFunction(WCAG.events.onContrastChange)) {
    WCAG.events.onContrastChange()
  }
  if (isFunction(WCAG.events.onContrastEnable) && value == 1) {
    WCAG.events.onContrastEnable()
  }
  if (isFunction(WCAG.events.onContrastDisable) && value == 0) {
    WCAG.events.onContrastDisable()
  }
}
